import ExcelJS from 'exceljs';
import type { FlatProduct } from '../services/api/domains/products/types';

/**
 * Fields that fan-out per flat row (one product can have many manufacturers,
 * facilities, grades, and therapeutic areas).  In Sheet 2 (deduplicated) these
 * are collapsed to comma-separated strings; all other fields are taken from the
 * first row for each distinct product_id.
 */
const ARRAY_FIELD_KEYS = new Set([
  'manufacturer_name',
  'manufacturing_status',
  'facility_name',
  'country_code',
  'facility_status',
  'grade_name',
  'therapeutic_area_name',
]);

/** Column specification shared by both sheets. */
export interface ExcelColumnDef {
  /** Field key on FlatProduct (matches FLAT_PRODUCT_FIELDS[*].key). */
  field: string;
  /** Display header used in the grid and in the Excel column header row. */
  headerName: string;
}

// ────────────────────────────────────────────────────────────────────────────
// Private helpers
// ────────────────────────────────────────────────────────────────────────────

/**
 * Convert a raw cell value to a safe string or number for writing to Excel.
 * - Booleans become "Yes" / "No"
 * - Dates become ISO strings
 * - null / undefined become empty string
 * - Numbers are preserved as numbers (not stringified) so Excel can sort/filter
 */
function sanitiseValue(value: unknown): string | number {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value;
  if (value instanceof Date) return value.toISOString();
  return String(value);
}

/** Apply bold header styling to the first row of a worksheet. */
function styleHeaderRow(row: ExcelJS.Row): void {
  row.font = { bold: true };
  row.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFD6E4F0' },
  } as ExcelJS.FillPattern;
  row.alignment = { vertical: 'middle' };
}

// ────────────────────────────────────────────────────────────────────────────
// Public API
// ────────────────────────────────────────────────────────────────────────────

/**
 * Build a two-sheet Excel workbook from a full set of flat product rows.
 *
 * Sheet 1 — "Flat Data"
 *   One row per FlatProduct record (raw denormalized Cartesian product).
 *
 * Sheet 2 — "Products"
 *   One row per distinct product_id.  Multi-value fields (manufacturer,
 *   facility, grade, therapeutic area) are collapsed to comma-separated
 *   distinct strings.  All other fields are taken from the first row for
 *   that product.
 *
 * Both sheets include only the columns listed in `columns` (the caller
 * filters by the current visibility model before calling this function).
 *
 * Cell values are written as plain strings or numbers — never as Excel formula
 * strings — so there is no formula injection risk.
 *
 * @param rows     All flat product rows (e.g. from fetchAllFlatProducts).
 * @param columns  Visible columns in display order (field key + headerName).
 * @returns        The workbook serialised as an .xlsx Blob.
 */
export async function buildExcelWorkbook(
  rows: FlatProduct[],
  columns: ExcelColumnDef[]
): Promise<Blob> {
  const workbook = new ExcelJS.Workbook();

  // ── Sheet 1: Flat Data ──────────────────────────────────────────────────
  const flatSheet = workbook.addWorksheet('Flat Data');

  flatSheet.columns = columns.map(col => ({
    header: col.headerName,
    key: col.field,
    width: 22,
  }));

  styleHeaderRow(flatSheet.getRow(1));

  for (const row of rows) {
    const rowData: Record<string, string | number> = {};
    for (const col of columns) {
      rowData[col.field] = sanitiseValue(row[col.field as keyof FlatProduct]);
    }
    flatSheet.addRow(rowData);
  }

  // Freeze header row so it stays visible while scrolling.
  flatSheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 1 }];

  // ── Sheet 2: Products (deduplicated) ────────────────────────────────────
  const dedupSheet = workbook.addWorksheet('Products');

  dedupSheet.columns = columns.map(col => ({
    header: col.headerName,
    key: col.field,
    width: 22,
  }));

  styleHeaderRow(dedupSheet.getRow(1));

  // Group rows by product_id, preserving the original insertion order.
  const productGroups = new Map<number, FlatProduct[]>();
  for (const row of rows) {
    const group = productGroups.get(row.product_id);
    if (group) {
      group.push(row);
    } else {
      productGroups.set(row.product_id, [row]);
    }
  }

  for (const group of Array.from(productGroups.values())) {
    const first = group[0];
    const rowData: Record<string, string | number> = {};

    for (const col of columns) {
      if (ARRAY_FIELD_KEYS.has(col.field)) {
        // Collect distinct non-empty values and join them.
        const seen = new Set<string>();
        for (const r of group) {
          const raw = r[col.field as keyof FlatProduct];
          if (raw !== null && raw !== undefined && raw !== '') {
            seen.add(String(sanitiseValue(raw)));
          }
        }
        rowData[col.field] = Array.from(seen).join(', ');
      } else {
        rowData[col.field] = sanitiseValue(
          first[col.field as keyof FlatProduct]
        );
      }
    }

    dedupSheet.addRow(rowData);
  }

  dedupSheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 1 }];

  // ── Serialise ───────────────────────────────────────────────────────────
  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}
